import React, { useCallback, useEffect, useState } from 'react';
import { holdersApi, apiErrorMessage } from '../../services/agentApi';
import { btn, input } from '../agent/agentUi';

export interface CertificateHolder {
  id: string;
  name: string;
  street1: string | null;
  street2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  email: string | null;
  descriptionOfOperations: string | null;
  isAdditionalInsured: boolean;
  waiverOfSubrogation: boolean;
}

const EMPTY = { name: '', street1: '', street2: '', city: '', state: '', postalCode: '', email: '', descriptionOfOperations: '', isAdditionalInsured: false, waiverOfSubrogation: false };

export const HolderForm: React.FC<{ value: typeof EMPTY; onChange: (v: typeof EMPTY) => void }> = ({ value, onChange }) => {
  const set = (k: keyof typeof EMPTY, v: string | boolean) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-2 gap-2 text-xs">
      <label className="col-span-2"><span className="text-gray-500">Holder name</span><input className={input} value={value.name} onChange={(e) => set('name', e.target.value)} /></label>
      <label className="col-span-2"><span className="text-gray-500">Street</span><input className={input} value={value.street1} onChange={(e) => set('street1', e.target.value)} /></label>
      <label className="col-span-2"><span className="text-gray-500">Street line 2</span><input className={input} value={value.street2} onChange={(e) => set('street2', e.target.value)} /></label>
      <label><span className="text-gray-500">City</span><input className={input} value={value.city} onChange={(e) => set('city', e.target.value)} /></label>
      <div className="grid grid-cols-2 gap-2">
        <label><span className="text-gray-500">State</span><input className={input} value={value.state} onChange={(e) => set('state', e.target.value)} /></label>
        <label><span className="text-gray-500">ZIP</span><input className={input} value={value.postalCode} onChange={(e) => set('postalCode', e.target.value)} /></label>
      </div>
      <label className="col-span-2"><span className="text-gray-500">Email (optional)</span><input className={input} value={value.email} onChange={(e) => set('email', e.target.value)} /></label>
      <label className="col-span-2"><span className="text-gray-500">Description of operations (default for this holder)</span><textarea className={input} rows={2} value={value.descriptionOfOperations} onChange={(e) => set('descriptionOfOperations', e.target.value)} /></label>
      <label className="flex items-center gap-1.5"><input type="checkbox" checked={value.isAdditionalInsured} onChange={(e) => set('isAdditionalInsured', e.target.checked)} /> Additional insured</label>
      <label className="flex items-center gap-1.5"><input type="checkbox" checked={value.waiverOfSubrogation} onChange={(e) => set('waiverOfSubrogation', e.target.checked)} /> Waiver of subrogation</label>
    </div>
  );
};

export const emptyHolder = () => ({ ...EMPTY });

/** Saved COI holders for a customer (so "send Acme's COI to the GC" isn't a re-type). */
const CertificateHoldersPanel: React.FC<{ customerId: string; onClose?: () => void }> = ({ customerId, onClose }) => {
  const [holders, setHolders] = useState<CertificateHolder[]>([]);
  const [editing, setEditing] = useState<{ id: string | null; value: typeof EMPTY } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    holdersApi.list(customerId).then((r) => setHolders(r.holders)).catch((err) => setError(apiErrorMessage(err)));
  }, [customerId]);
  useEffect(load, [load]);

  const save = async () => {
    if (!editing) return;
    setError(null);
    try {
      if (editing.id) await holdersApi.update(editing.id, editing.value);
      else await holdersApi.create(customerId, editing.value);
      setEditing(null);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save'));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Certificate holders</h3>
        <div className="flex gap-2">
          {!editing && <button className={btn.secondary} onClick={() => setEditing({ id: null, value: emptyHolder() })}>Add holder</button>}
          {onClose && <button className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Close">✕</button>}
        </div>
      </div>
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      {editing && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3">
          <HolderForm value={editing.value} onChange={(value) => setEditing({ ...editing, value })} />
          <div className="flex gap-2">
            <button className={btn.primary} onClick={save} disabled={!editing.value.name.trim()}>Save</button>
            <button className={btn.secondary} onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}
      {holders.length === 0 && !editing ? (
        <p className="text-sm text-gray-500">No saved holders yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {holders.map((h) => (
            <li key={h.id} className="py-2 flex items-start justify-between gap-2">
              <div className="text-sm">
                <div className="text-gray-900 dark:text-white font-medium">{h.name}</div>
                <div className="text-xs text-gray-500">{[h.street1, h.city, h.state, h.postalCode].filter(Boolean).join(', ')}</div>
                <div className="text-[11px] text-gray-500">{[h.isAdditionalInsured && 'Additional insured', h.waiverOfSubrogation && 'Waiver of subrogation'].filter(Boolean).join(' · ')}</div>
              </div>
              <div className="flex gap-2 text-xs">
                <button className="text-primary hover:underline" onClick={() => setEditing({ id: h.id, value: { ...EMPTY, ...Object.fromEntries(Object.entries(h).map(([k, v]) => [k, v ?? (typeof (EMPTY as any)[k] === 'boolean' ? false : '')])) } as typeof EMPTY })}>Edit</button>
                <button className="text-red-600 dark:text-red-400 hover:underline" onClick={() => holdersApi.remove(h.id).then(load)}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CertificateHoldersPanel;
